import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatValue, pctColor } from "@/lib/api";
import DmeTag from "@/components/DmeTag";
import MarketVectors from "@/components/MarketVectors";
import SellHighBlueprint from "@/components/SellHighBlueprint";
import PlayerHeadshot, { TeamPill, PosRank } from "@/components/PlayerHeadshot";
import { useFormat, FORMATS } from "@/contexts/FormatContext";
import { Loader2, ArrowLeft, TrendingUp } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const FMT_LABEL = Object.fromEntries(FORMATS.map((f) => [f.id, f.label]));

export default function PlayerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const { format } = useFormat();

  useEffect(() => {
    setLoading(true);
    api.get(`/players/${id}`).then(r => setP(r.data)).finally(() => setLoading(false));
  }, [id, format]);

  if (loading) return <div className="card-bordered p-12 flex justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div>;
  if (!p || !Array.isArray(p.history) || p.history.length === 0) return <div className="card-bordered p-12 text-center text-[var(--text-2)]">Player not found.</div>;

  const start = p.history[0]?.value || 0;
  const end = p.history[p.history.length - 1]?.value || 0;
  const pct = start > 0 ? ((end - start) / start) * 100 : 0;
  const breakdown = Array.isArray(p.format_breakdown) ? p.format_breakdown : [];
  const isPick = p.position === "PICK";

  return (
    <div className="space-y-6 fade-up">
      <Link to="/app/trade-calculator" className="text-xs text-[var(--text-3)] inline-flex items-center gap-1 hover:text-white" data-testid="player-back">
        <ArrowLeft className="w-3 h-3" /> Back
      </Link>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-end gap-5 min-w-0">
          {!isPick && (
            <PlayerHeadshot playerId={p.player_id} name={p.name} team={p.team} size={88} thumb={false} className="border border-[var(--border)]" />
          )}
          <div className="min-w-0">
            <div className="text-[var(--accent)] text-xs tracking-widest mb-1">// PLAYER · {p.position} · {p.team}</div>
            <h1 className="font-display text-5xl font-bold" data-testid="pd-name">{p.name}</h1>
            <div className="flex items-center gap-2 mt-2">
              {!isPick && <PosRank rank={p.pos_rank} delta={p.pos_rank_delta} onFilter={(pos) => nav(`/app/values?pos=${pos}`)} />}
              {!isPick && <TeamPill team={p.team} />}
              <span className="text-[var(--text-2)] text-sm">Age {p.age || "—"}</span>
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs tracking-widest text-[var(--text-3)] flex items-center justify-end gap-1.5">CURRENT VALUE <DmeTag /></div>
          <div className="font-mono text-5xl font-semibold text-[var(--accent)]" data-testid="pd-value">{formatValue(p.value)}</div>
          <div className={`text-sm tabular ${pctColor(pct)}`} data-testid="pd-pct">
            {pct > 0 ? "+" : ""}{pct.toFixed(1)}% / 30d
          </div>
        </div>
      </header>

      {breakdown.length > 0 && !isPick && (
        <section data-testid="format-breakdown">
          <div className="text-xs tracking-widest text-[var(--text-3)] mb-3">FORMAT VALUE SHIFT · VS 1QB BASELINE</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {breakdown.map((b) => {
              const active = b.format === format;
              const up = b.delta_pct > 0;
              const flat = Math.abs(b.delta_pct) < 0.1;
              return (
                <div
                  key={b.format}
                  data-testid={`fmt-chip-${b.format}`}
                  className={`card-bordered p-4 transition-colors ${active ? "border-[var(--accent)] bg-[var(--accent)]/5" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] tracking-widest text-[var(--text-3)]">{FMT_LABEL[b.format] || b.format.toUpperCase()}</div>
                    {active && <span className="text-[9px] tracking-widest text-[var(--accent)]">ACTIVE</span>}
                  </div>
                  <div className="font-mono text-2xl mt-1" data-testid={`fmt-value-${b.format}`}>{formatValue(b.value)}</div>
                  <div
                    className={`text-xs mt-1 tabular ${flat ? "text-[var(--text-3)]" : up ? "text-[var(--accent)]" : "text-red-400"}`}
                    data-testid={`fmt-delta-${b.format}`}
                  >
                    {flat ? "baseline" : `${up ? "+" : ""}${b.delta_pct}% in ${FMT_LABEL[b.format] || b.format}`}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {!isPick && Array.isArray(p.valuation_vectors) && (
        <MarketVectors vectors={p.valuation_vectors} divergence={p.divergence} format={p.format} />
      )}

      {!isPick && p.divergence?.flagged && p.divergence?.kind === "sell_high" && (
        <SellHighBlueprint playerId={p.player_id} playerName={p.name} />
      )}

      <section className="card-bordered p-6">
        <div className="text-xs tracking-widest text-[var(--text-3)] mb-4 flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5" /> 30-DAY VALUE TREND
        </div>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <AreaChart data={p.history} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="pd-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#16A34A" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#16A34A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="#9CA3AF" fontSize={10} tickFormatter={(v) => v.slice(5)} />
              <YAxis stroke="#9CA3AF" fontSize={10} />
              <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E5E7EB", color: "#111827" }} labelStyle={{ color: "#6B7280" }} />
              <Area type="monotone" dataKey="value" stroke="#16A34A" strokeWidth={2} fill="url(#pd-grad)" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>
    </div>
  );
}
